

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewTrainingCompletionModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        training_module_id: '',
        guide_id: '',
        supplier_id: '',
        completion_status: 'Not Started',
        completion_date: '',
        remarks: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [trainingModules, setTrainingModules] = useState([]);
    const [guides, setGuides] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [loadingModules, setLoadingModules] = useState(false);
    const [loadingGuides, setLoadingGuides] = useState(false);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchTrainingModules();
            fetchGuides();
            fetchSuppliers();
        }
    }, [isOpen]);

    const fetchTrainingModules = async () => {
        setLoadingModules(true);
        try {
            const response = await axiosInstance.get('/training-modules/');
            setTrainingModules(response.data);
        } catch (error) {
            console.error('Error fetching training modules:', error);
        } finally {
            setLoadingModules(false);
        }
    };

    const fetchGuides = async () => {
        setLoadingGuides(true);
        try {
            const response = await axiosInstance.get('/contacts/?dropdown=true');
            setGuides(response.data || []);
        } catch (error) {
            console.error('Error fetching guides:', error);
        } finally {
            setLoadingGuides(false);
        }
    };

    const fetchSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            setSuppliers(response.data || []);
        } catch (error) {
            console.error('Error fetching suppliers:', error);
        } finally {
            setLoadingSuppliers(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.training_module_id) errors.training_module_id = 'Training Module is required';
        if (!formData.completion_status) errors.completion_status = 'Completion Status is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                training_module_id: parseInt(formData.training_module_id),
                guide_id: formData.guide_id ? parseInt(formData.guide_id) : null,
                supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null,
                completion_date: formData.completion_date || null,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData({
                training_module_id: '',
                guide_id: '',
                supplier_id: '',
                completion_status: 'Not Started',
                completion_date: '',
                remarks: ''
            });
        } catch (error) {
            console.error('Error saving Training Completion:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Training Completion',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Training Completion</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.training_module_id} required>
                                        <InputLabel>Training Module</InputLabel>
                                        <Select
                                            name="training_module_id"
                                            value={formData.training_module_id}
                                            onChange={handleInputChange}
                                            label="Training Module"

                                            disabled={loadingModules}
                                        >
                                            <MenuItem value=""><em>Select a Training Module</em></MenuItem>
                                            {trainingModules.map(tm => (
                                                <MenuItem key={tm.id} value={tm.id}>{tm.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.training_module_id && <MDTypography variant="caption" color="error">{formErrors.training_module_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Completion Status</InputLabel>
                                        <Select
                                            name="completion_status"
                                            value={formData.completion_status}
                                            onChange={handleInputChange}
                                            label="Completion Status"

                                        >
                                            <MenuItem value="Not Started">Not Started</MenuItem>
                                            <MenuItem value="In Progress">In Progress</MenuItem>
                                            <MenuItem value="Completed">Completed</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Guide</InputLabel>
                                        <Select
                                            name="guide_id"
                                            value={formData.guide_id}
                                            onChange={handleInputChange}
                                            label="Guide"

                                            disabled={loadingGuides}
                                        >
                                            <MenuItem value=""><em>Select a Guide</em></MenuItem>
                                            {guides.map(g => (
                                                <MenuItem key={g.id} value={g.id}>
                                                    {g.first_name} {g.last_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Supplier</InputLabel>
                                        <Select
                                            name="supplier_id"
                                            value={formData.supplier_id}
                                            onChange={handleInputChange}
                                            label="Supplier"

                                            disabled={loadingSuppliers}
                                        >
                                            <MenuItem value=""><em>Select a Supplier</em></MenuItem>
                                            {suppliers.map(s => (
                                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Completion Date"
                                        name="completion_date"
                                        type="date"
                                        value={formData.completion_date}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Remarks"
                                        name="remarks"
                                        multiline
                                        rows={4}
                                        value={formData.remarks}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Training Completion'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewTrainingCompletionModal;

